<template>
  <ToggleCard
    v-bind="{ disabled, name, variant }"
    v-model="model"
    class="high-frequency-game-add-on"
    container-id="high_frequency_game_add_on_form"
    :info-button-aria-label="$t('infobutton.conveniencefee.label')"
    :info-headline="
      $t(`highfrequencygame.addontoggle.${name}.info.headline`, {
        addOn: translatedAddOn,
      })
    "
    :info-text="
      $t(`highfrequencygame.addontoggle.${name}.info.text`, {
        addOn: translatedAddOn,
      })
    "
    :switch-aria-label-text="$t('highfrequencygame.addontoggle.default.switch')"
  >
    <template #headline>{{ translatedAddOn }}</template>

    <div class="high-frequency-game-add-on-footer flex flex-grow items-center justify-between">
      <span v-if="disabled" class="caption-regular text-primary-primary">
        {{ $t(`highfrequencygame.addontoggle.${name}.disabled.text`) }}
      </span>

      <template v-else>
        <span class="caption-regular text-secondary-secondary">
          {{ $t(`highfrequencygame.addontoggle.${name}.text`) }}
        </span>

        <span
          class="high-frequency-game-add-on-badge paragraph-small-text py-050 px-100 rounded-s bg-secondary-secondary text-primary-primary text-right"
          :class="{ 'high-frequency-game-add-on-badge-highlight': isHighlightBadge }"
        >
          {{ $t(`highfrequencygame.addontoggle.${name}.badge.text`) }}
        </span>
      </template>
    </div>
  </ToggleCard>
</template>

<script lang="ts" setup>
import type { HighFrequency_Price } from '#api/shared/@types/generated/backend/graphql-schema-types'
import type { ToggleCardVariantStyle } from '~/components/Toggle/types'

type Props = { disabled?: boolean; name: string | VariantKey; price: HighFrequency_Price }

type VariantKey = keyof typeof TOGGLE_CARD_VARIANTS

const TOGGLE_CARD_VARIANTS = {
  bullseye: {
    gradient: {
      bottom: { color: '#d3143333', stop: '101.98%' },
      top: { color: '#D31433', stop: '0.13%' },
    },
    illustration: {
      active: 'bullseye-active',
      inactive: 'bullseye',
    },
    opacity: { active: 0.2, inactive: 0.04 },
  },
  kenoBonus: {
    gradient: {
      bottom: { color: '#fbcd0c66', stop: '101.98%' },
      top: { color: '#FBCD0C', stop: '0.13%' },
    },
    illustration: {
      active: 'bonus-active',
      inactive: 'bonus',
    },
    opacity: { active: 0.2, inactive: 0.04 },
  },
  kenoBooster: {
    gradient: {
      bottom: { color: '#fbcd0c66', stop: '101.98%' },
      top: { color: '#FBCD0C', stop: '0.13%' },
    },
    illustration: {
      active: 'booster-active',
      inactive: 'booster',
    },
    opacity: { active: 0.2, inactive: 0.04 },
  },
  kenoMultiplier: {
    gradient: {
      bottom: { color: '#5AB0DD', stop: '101.98%' },
      top: { color: '#5AB0DD', stop: '0.13%' },
    },
    illustration: {
      active: 'multiplier-active',
      inactive: 'multiplier',
    },
    opacity: { active: 0.2, inactive: 0.04 },
  },
  specialKeno: {
    gradient: {
      bottom: { color: '#10bfff33', stop: '101.98%' },
      top: { color: '#10BFFF', stop: '0.13%' },
    },
    illustration: {
      active: 'special-keno-active',
      inactive: 'special-keno',
    },
    opacity: { active: 0.2, inactive: 0.04 },
  },
} as const satisfies Record<string, ToggleCardVariantStyle>

const { disabled = false, name, price } = defineProps<Props>()
const model = defineModel({ default: false, type: Boolean })

const { t } = useI18n()

const translatedAddOn = computed(() => t(`highfrequencygame.addon.${name}`))

const isHighlightBadge = computed(
  () => price.__typename === 'HighFrequency_FixedPrice' && price.amount.value <= 0,
)

const variant = computed(() => {
  if (!hasToggleCardVariant(name)) return
  return TOGGLE_CARD_VARIANTS[name]
})

const hasToggleCardVariant = (name: string): name is VariantKey =>
  TOGGLE_CARD_VARIANTS[name as VariantKey] !== undefined
</script>

<style lang="scss" scoped>
.high-frequency-game-add-on {
  min-height: 6.75rem;

  &-footer {
    min-height: 2rem;
  }

  &-badge {
    text-wrap: nowrap;

    &-highlight {
      @include token-paragraph-bold;
    }
  }
}
</style>
